import { Named } from 'app/model/base/named';
import { Metamodel } from 'app/model/base/model';
import { TriggerTypeOptions } from 'app/shared/components/trigger/trigger-configuration/trigger-configuration.options';
import { Trigger } from 'app/model/trigger/trigger';
import { AdapterContext, arrayIsNullOrEmpty, IResourceContext, LinkedService, SqlPool, stringIsNullOrEmpty } from 'shell-models-lib';
import { ModelStoreService } from 'app/services/model-store.service';
import { IModel } from 'app/model/base/annotation';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { take } from 'rxjs/operators';
import { SqlSink } from 'app/model/pipeline/copysink/sql.copysink';
import { SqlCopySource } from 'app/model/pipeline/copysource/sql.copysource';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

export async function convertToModelAsync<T extends Named>(
    contextData: IResourceContext,
    name: string,
    properties: {
        type: string;
    },
    flattenProperties: boolean = false,
    objectId?: string
): Promise<T> {
    const model =
        properties.type === TriggerTypeOptions.Scheduled || properties.type === TriggerTypeOptions.TumblingWindow
            ? Metamodel.modelByName(Metamodel.modelFor(Trigger).name)
            : Metamodel.modelByName(properties.type);

    const modelStoreService = await getServiceInstance<ModelStoreService>(contextData, 'ModelStoreService');
    const defaultContext = AdapterContext.defaultContextOf(contextData);
    // Special handling for linked service.
    if (model.concept && model.concept.name === Metamodel.modelFor(LinkedService).name) {
        const linkedService = (await modelStoreService.linkedServices$.pipe(take(1)).toPromise()).find((e) =>
            defaultContext.adapterConfig.referenceById && objectId != null ? e.objectId === objectId : e.name === name
        );
        if (linkedService) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return linkedService as any;
        }
    }

    // Special handling for sql computes.
    if (model.concept && model.concept.name === Metamodel.modelFor(SqlPool).name) {
        const sqlPool = (await modelStoreService.sqlPool$.pipe(take(1)).toPromise()).find((e) => e.name === name);
        if (sqlPool) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return sqlPool as any;
        }
    }

    const adapter = defaultContext.asyncAdapterFor(model) ?? defaultContext.adapterFor(model);
    return !flattenProperties
        ? await adapter.toModel({
              name,
              objectId,
              properties
          })
        : await adapter.toModel({
              name,
              objectId,
              ...properties
          });
}

export async function convertToResourceAsync<
    T = {
        name: string;
        objectId?: string;
        properties: { type: string; typeProperties: Record<string, unknown> };
        typeProperties?: Record<string, unknown>;
    }
>(contextData: IResourceContext, named: Named): Promise<T> {
    const model = Metamodel.modelOf(named);

    const adapter =
        AdapterContext.defaultContextOf(contextData).asyncAdapterFor(model) ??
        AdapterContext.defaultContextOf(contextData).adapterFor(model);

    // special handling for sql pool
    const resource = await adapter.toResource(named);
    if (Metamodel.modelOf(named).name === adfModelTypeMap.sqlPool.connectionType) {
        resource.properties = {
            type: Metamodel.modelOf(named).name
        };
    }

    return resource;
}

export function getModelByLinkedServiceType(linkedServiceType: string, ancestor: IModel): IModel {
    return Metamodel.models().find((model) => {
        if (ancestor.isAncestor(model)) {
            const lsModel = model.tag('linkedServiceModel') as IModel | IModel[];
            if (Array.isArray(lsModel)) {
                return lsModel.find((ls) => ls.name === linkedServiceType) != null;
            } else if (lsModel != null) {
                return lsModel.name === linkedServiceType;
            }
        }
        return false;
    });
}

export function getReferencedModelByDatasetType(datasetType: string, ancestor: IModel): IModel {
    const datasetModel = Metamodel.modelByName(datasetType);
    return Metamodel.models().find((m) => {
        if (ancestor.isAncestor(m)) {
            const datasetModelTag = m.tag('datasetModel');
            if (
                datasetModelTag === datasetModel ||
                (Array.isArray(datasetModelTag) && datasetModelTag.find((modelInTag) => modelInTag === datasetModel))
            ) {
                if (m.name === Metamodel.modelFor(SqlSink).name || m.name === Metamodel.modelFor(SqlCopySource).name) {
                    // use seperate copy source/sink model for sqlv2
                    return false;
                }
                return true;
            }
        }
        return false;
    });
}

export function getCompressionModelFromType(compressionType: string, ancestorModel: IModel): IModel {
    if (!stringIsNullOrEmpty(compressionType)) {
        const compressionSettingsModel = Metamodel.models().filter((model) => {
            if (ancestorModel.isAncestor(model)) {
                const compressionTypeTag = model.tag('compressionType');
                if (compressionTypeTag === compressionType) {
                    return true;
                }
            }
            return false;
        });
        return !arrayIsNullOrEmpty(compressionSettingsModel) ? compressionSettingsModel[0] : undefined;
    }
    return undefined;
}